import { IMedicalReferral } from 'API/medicalReferrals';
import { isAfter, isBefore, isWithinInterval } from 'date-fns';
import { REFERRAL_STATUS } from 'Enum/medicalReferrals';
import { IAdvancedSearchState } from './AdvancedSearchReducer';
import { replaceZerosWithHyphen } from 'Utils/replaceZerosWithHyphen';

export interface IReferralsState {
  validReferrals: IMedicalReferral[];
  otherReferrals: IMedicalReferral[];
  filteredReferrals: IMedicalReferral[];
}

export function ReferralReducer(state: IReferralsState, action: any) {
  switch (action.type) {
    case 'INITIALIZATION': {
      const referralList = action.payload.value.map(
        (referral: IMedicalReferral) => ({
          ...referral,
          statusCode: referral.statusCode === 7 ? 4 : referral.statusCode,
        })
      );

      return DivideReferralListToValidAndOtherList(referralList);
    }
    case 'SEARCH_REFERRAL': {
      const filteredReferrals = SearchReferralBySubject(
        state,
        action.payload.value
      );
      return { ...state, filteredReferrals };
    }
    case 'ADVANCED_SEARCH': {
      const { validReferrals, otherReferrals } = state;
      const filteredReferrals = AdvancedSearch(
        { validReferrals, otherReferrals },
        action.payload.value
      );
      return { ...state, filteredReferrals };
    }
    case 'RESET': {
      const filteredReferrals: IMedicalReferral[] = [];
      return { ...state, filteredReferrals };
    }
  }
  return state;
}

/**
 * Divides valid and other by the validation date. present valid are valid and all others are others even if future valid.
 * @param referralList Array of all existing referrals.
 * @returns IReferralsState organized object.
 */
function DivideReferralListToValidAndOtherList(
  referralList: IMedicalReferral[]
) {
  const referrals: IReferralsState = {
    validReferrals: [],
    otherReferrals: [],
    filteredReferrals: [],
  };
  // First we filter for the valid referrals and all the rest are immediately other.
  const validReferrals = referralList?.filter(
    (referral) =>
      isAfter(new Date(referral.validToDate), new Date(Date.now())) &&
      referral.statusCode === REFERRAL_STATUS.APPROVED
  );
  referrals.validReferrals.push(...validReferrals);
  referrals.otherReferrals.push(
    ...referralList.filter((referral) => !validReferrals.includes(referral))
  );
  referrals.validReferrals = [...sortByOpenDate(referrals.validReferrals)];
  referrals.otherReferrals = [...sortByOpenDate(referrals.otherReferrals)];
  return referrals;
}

/**
 * A basic search algorithm with 'INCLUDE' function. Search is based on part field.
 * @param referralList Referrals State.
 * @param searchValue Search string value.
 * @returns
 */
function SearchReferralBySubject(
  referralList: IReferralsState,
  searchValue: string
) {
  const filteredReferrals: IMedicalReferral[] = [];
  filteredReferrals.push(
    ...referralList.validReferrals.filter((referral: IMedicalReferral) =>
      referral.part.includes(searchValue)
    ),
    ...referralList.otherReferrals.filter((referral: IMedicalReferral) =>
      referral.part.includes(searchValue)
    )
  );
  return filteredReferrals;
}

/**
 * Advanced search via all search fields if not null.
 * @param referrals Referral none filtered list.
 * @param searchFields Advanced Search State object.
 * @returns Filtered referral list.
 */
function AdvancedSearch(
  referrals: {
    validReferrals: IMedicalReferral[];
    otherReferrals: IMedicalReferral[];
  },
  searchFields: IAdvancedSearchState
) {
  const filteredReferrals: IMedicalReferral[] = [];
  filteredReferrals.push(
    ...advancedFilterBySearchFields(referrals.validReferrals, searchFields),
    ...advancedFilterBySearchFields(referrals.otherReferrals, searchFields)
  );
  return filteredReferrals;
}

/**
 * Filters a given referral list according to searchFields.
 * @param referralList Referral none filtered list.
 * @param searchFields Advanced Search State object.
 * @returns Filtered referral list.
 */
function advancedFilterBySearchFields(
  referralList: IMedicalReferral[],
  searchFields: IAdvancedSearchState
) {
  const {
    requestState,
    requestNumber,
    providerName,
    dateRange,
    requestValidation,
  } = searchFields;
  const filteredReferrals = referralList.filter(
    (referral: IMedicalReferral) => {
      if (
        (requestValidation.presentValidated ||
          requestValidation.pastValidated ||
          requestValidation.futureValidated) &&
        referral.statusCode !== REFERRAL_STATUS.APPROVED
      )
        return false;
      if (requestState !== null && requestState !== referral.statusCode)
        return false;
      if (
        requestNumber !== null &&
        !(
          replaceZerosWithHyphen(referral.requestNumber.toString()) ===
          requestNumber.toString()
        )
      )
        return false;
      if (providerName !== null && providerName !== referral.supplierName)
        return false;

      // Date handler , checks all possible 3 ways , between 2 dates , after start , before end.
      const openDate = new Date(referral.openDate);
      if (dateRange.start && dateRange.end) {
        if (isAfter(dateRange.start, dateRange.end)) return false;
        return isWithinInterval(openDate, {
          start: new Date(dateRange.start),
          end: new Date(dateRange.end),
        });
      } else if (dateRange.start && !dateRange.end)
        return isAfter(openDate, new Date(dateRange.start));
      else if (dateRange.end)
        return isBefore(openDate, new Date(dateRange.end));

      return dateValidationHandler(requestValidation, referral);
    }
  );
  return filteredReferrals;
}

/**
 * Validation Date Handler - According to past,present or future validated only referrals.
 * @param requestValidation Sub field object of Advanced Search State.
 * @param referral Single referral object.
 * @returns True/False
 */
function dateValidationHandler(
  requestValidation: {
    presentValidated: boolean;
    pastValidated: boolean;
    futureValidated: boolean;
  },
  referral: IMedicalReferral
) {
  const { presentValidated, pastValidated, futureValidated } =
    requestValidation;
  if (presentValidated || pastValidated || futureValidated) {
    if (
      presentValidated &&
      referral.validFromDate &&
      referral.validToDate &&
      isWithinInterval(new Date(), {
        start: new Date(referral.validFromDate),
        end: new Date(referral.validToDate),
      })
    )
      return true;
    if (
      pastValidated &&
      referral.validToDate &&
      isAfter(new Date(), new Date(referral.validToDate))
    )
      return true;
    if (
      futureValidated &&
      referral.validFromDate &&
      isBefore(new Date(), new Date(referral.validFromDate))
    )
      return true;
    return false;
  }
  return true;
}

/**
 * Sorts the referral list by the date field - openDate in descending order.
 * @param referralList Referral None Filtered List.
 * @returns Sorted Referral List.
 */
function sortByOpenDate(referralList: IMedicalReferral[]) {
  const sortedReferrals = referralList.sort(
    (a: IMedicalReferral, b: IMedicalReferral) =>
      new Date(b.openDate).getTime() - new Date(a.openDate).getTime()
  );
  return sortedReferrals;
}
